import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, MotionConfig, useReducedMotion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ThemeProvider, useTheme } from "@/components/ThemeContext";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Solutions from "@/pages/Solutions";
import Outsourcing from "@/pages/Outsourcing";
import OutsourcingDetail from "@/pages/OutsourcingDetail";
import About from "@/pages/About";
import Careers from "@/pages/Careers";
import CareerDetail from "@/pages/CareerDetail";
import Contact from "@/pages/Contact";

const ScrollToTop = ({ lenisRef }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    if (lenisRef?.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    }
  }, [pathname, lenisRef]);

  return null;
};

const RouteMetadata = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const canonicalUrl = new URL(pathname, window.location.origin).href;
    const canonical = document.querySelector('link[rel="canonical"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const pageHeading = document.querySelector("main h1")?.textContent?.replace(/\s+/g, " ").trim();

    if (canonical) canonical.href = canonicalUrl;
    if (ogUrl) ogUrl.content = canonicalUrl;

    document.title = pathname === "/"
      ? "Spark Link IT Solutions | Custom Software & AI"
      : `${pageHeading || "IT Solutions"} | Spark Link IT Solutions`;
  }, [pathname]);

  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/solutions/:slug" element={<ServiceDetail />} />
        <Route path="/outsourcing" element={<Outsourcing />} />
        <Route path="/outsourcing/:slug" element={<OutsourcingDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/careers/:slug" element={<CareerDetail />} />
        <Route path="/contact" element={<Contact />} />
      </Routes>
    </AnimatePresence>
  );
};

function AppContent() {
  const lenisRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { theme } = useTheme();

  useEffect(() => {
    if (reduceMotion) return undefined;

    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [reduceMotion]);

  return (
    <MotionConfig reducedMotion="user">
      <div className="App">
        <BrowserRouter>
          <RouteMetadata />
          <ScrollToTop lenisRef={lenisRef} />
          <Navbar />
          <AnimatedRoutes />
          <Footer />
          <Toaster position="bottom-right" theme={theme === "night" ? "dark" : "light"} />
        </BrowserRouter>
      </div>
    </MotionConfig>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
